import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import DashboardLayout from "./DashboardLayout";

const API = "/api";

const cardStyle = {
  background: "#fff",
  borderRadius: "18px",
  border: "1px solid rgba(148, 163, 184, 0.16)",
  boxShadow: "0 18px 36px rgba(15, 23, 42, 0.08)",
};

const thStyle = {
  padding: "13px 12px",
  textAlign: "center",
  color: "#7c2d3a",
  background: "rgba(255,247,237,0.86)",
  borderBottom: "1px solid rgba(148, 163, 184, 0.18)",
  fontSize: "13px",
};

const tdStyle = {
  padding: "12px",
  textAlign: "center",
  verticalAlign: "middle",
  borderBottom: "1px solid rgba(148, 163, 184, 0.12)",
  color: "#334155",
  fontSize: "13px",
};

const statusStyle = (status) => {
  const normalized = String(status || "pending").toLowerCase();
  const map = {
    approved: { background: "#dcfce7", color: "#166534" },
    rejected: { background: "#fee2e2", color: "#991b1b" },
    pending: { background: "#fef3c7", color: "#92400e" },
  };
  return {
    ...(map[normalized] || map.pending),
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "86px",
    padding: "6px 10px",
    borderRadius: "999px",
    fontWeight: 800,
    textTransform: "capitalize",
  };
};

const showToast = (icon, title, text = "") => Swal.fire({
  icon,
  title,
  text,
  timer: 2800,
  timerProgressBar: true,
  showConfirmButton: false,
  confirmButtonColor: icon === "error" ? "#dc2626" : icon === "warning" ? "#f59e0b" : "#16a34a",
});

function getAdminActor() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user.name || user.admin_type || user.login_id || user.email || "Admin";
  } catch (error) {
    return "Admin";
  }
}

function stars(rating) {
  const count = Math.max(1, Math.min(5, Number(rating || 0)));
  return "★".repeat(count) + "☆".repeat(5 - count);
}

export default function AdminFeedbackBox() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [actionItem, setActionItem] = useState(null);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/student-feedbacks`, { params: { _ts: Date.now() } });
      setRows(res.data || []);
    } catch (error) {
      showToast("error", "Load Failed", error.response?.data?.message || "Feedback data load failed.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) => `${row.student_name || ""} ${row.registration_no || ""} ${row.feedback || ""} ${row.status || ""}`.toLowerCase().includes(needle));
  }, [rows, search]);

  const editFeedback = async (item) => {
    const result = await Swal.fire({
      title: "Edit Feedback",
      html: `
        <select id="feedback-rating" class="swal2-input" style="width:80%;height:44px">
          ${[5, 4, 3, 2, 1].map((rating) => `<option value="${rating}" ${Number(item.rating) === rating ? "selected" : ""}>${rating} Star</option>`).join("")}
        </select>
        <textarea id="feedback-description" class="swal2-textarea" placeholder="Feedback description">${String(item.feedback || "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</textarea>
      `,
      showCancelButton: true,
      confirmButtonText: "Update",
      confirmButtonColor: "#7c2d3a",
      cancelButtonColor: "#64748b",
      preConfirm: () => {
        const rating = document.getElementById("feedback-rating")?.value;
        const feedback = document.getElementById("feedback-description")?.value?.trim();
        if (!rating || !feedback) {
          Swal.showValidationMessage("Rating and feedback description are required");
          return false;
        }
        return { rating, feedback };
      },
    });

    if (!result.isConfirmed) return;

    try {
      await axios.put(`${API}/admin/student-feedbacks/${item.id}`, result.value);
      await fetchRows();
      showToast("success", "Feedback Updated", "Student feedback updated successfully.");
    } catch (error) {
      showToast("error", "Update Failed", error.response?.data?.message || "Feedback update failed.");
    }
  };

  const updateStatus = async (item, status) => {
    const label = status === "approved" ? "Approve" : "Reject";
    setActionItem(null);
    const result = await Swal.fire({
      icon: status === "approved" ? "question" : "warning",
      title: `${label} Feedback?`,
      input: "textarea",
      inputPlaceholder: "Remarks",
      inputValidator: (value) => (!value?.trim() ? "Remarks are required" : null),
      showCancelButton: true,
      confirmButtonText: label,
      cancelButtonText: "Cancel",
      confirmButtonColor: status === "approved" ? "#16a34a" : "#dc2626",
      cancelButtonColor: "#64748b",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      await axios.post(`${API}/admin/student-feedbacks/${item.id}/status`, {
        status,
        remark: result.value,
        updated_by: getAdminActor(),
      });
      setActionItem(null);
      await fetchRows();
      showToast("success", `Feedback ${label}d`, `${item.student_name || "Student"} feedback has been ${status}.`);
    } catch (error) {
      showToast("error", "Action Failed", error.response?.data?.message || "Feedback action failed.");
    }
  };

  const deleteFeedback = async (item) => {
    setActionItem(null);
    const result = await Swal.fire({
      icon: "warning",
      title: "Delete Feedback?",
      text: `${item.student_name || "Student"} feedback will be removed.`,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(`${API}/admin/student-feedbacks/${item.id}`);
      setActionItem(null);
      await fetchRows();
      showToast("success", "Deleted", "Feedback deleted successfully.");
    } catch (error) {
      showToast("error", "Delete Failed", error.response?.data?.message || "Feedback delete failed.");
    }
  };

  return (
    <DashboardLayout>
      <style>
        {`
          .swal2-container {
            z-index: 99999 !important;
          }
        `}
      </style>
      <div style={{ display: "grid", gap: "18px" }}>
        <div style={{ ...cardStyle, padding: "24px", background: "linear-gradient(135deg, #4e1b24 0%, #7c2d3a 52%, #c2410c 100%)", color: "#fff" }}>
          <h2 style={{ margin: 0 }}>Feedback Box</h2>
          <p style={{ margin: "8px 0 0", color: "rgba(255,255,255,0.82)" }}>Student rating and feedback review workspace.</p>
        </div>

        <div style={{ ...cardStyle, padding: "16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center", flexWrap: "wrap", marginBottom: "14px" }}>
            <input className="form-control" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search student, registration, feedback" style={{ maxWidth: "360px", borderRadius: "12px" }} />
            <button type="button" className="btn btn-outline-danger" onClick={fetchRows}>
              <i className="bi bi-arrow-clockwise me-1"></i> Refresh
            </button>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "980px" }}>
              <thead>
                <tr>
                  <th style={thStyle}>Sl No</th>
                  <th style={thStyle}>Student Picture</th>
                  <th style={thStyle}>Student Name</th>
                  <th style={thStyle}>Rating</th>
                  <th style={thStyle}>Feedback</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Edit</th>
                  <th style={thStyle}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="8" style={tdStyle}>Loading feedback...</td></tr>
                ) : null}
                {!loading && filteredRows.length === 0 ? (
                  <tr><td colSpan="8" style={tdStyle}>No feedback found.</td></tr>
                ) : null}
                {filteredRows.map((item, index) => (
                  <tr key={item.id}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>
                      {item.student_photo_url ? (
                        <img src={item.student_photo_url} alt={item.student_name || "Student"} style={{ width: "54px", height: "54px", borderRadius: "14px", objectFit: "cover", border: "1px solid rgba(148,163,184,0.22)" }} />
                      ) : (
                        <span style={{ width: "54px", height: "54px", borderRadius: "14px", display: "inline-flex", alignItems: "center", justifyContent: "center", background: "#fff7ed", color: "#7c2d3a", fontWeight: 900 }}>
                          {(item.student_name || "S").slice(0, 1).toUpperCase()}
                        </span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <strong style={{ display: "block", color: "#0f172a" }}>{item.student_name || "-"}</strong>
                      <span style={{ color: "#64748b", fontSize: "12px" }}>{item.registration_no || item.student_login_id || "-"}</span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ color: "#f59e0b", fontWeight: 900, letterSpacing: "1px" }}>{stars(item.rating)}</span>
                      <strong style={{ display: "block", color: "#0f172a" }}>{item.rating}/5</strong>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "left", minWidth: "260px" }}>{item.feedback}</td>
                    <td style={tdStyle}>
                      <span style={statusStyle(item.status)}>{item.status || "pending"}</span>
                      {item.updated_by ? <small style={{ display: "block", marginTop: "5px", color: "#64748b" }}>By {item.updated_by}</small> : null}
                    </td>
                    <td style={tdStyle}>
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => editFeedback(item)}>
                        <i className="bi bi-pencil-square me-1"></i>Edit
                      </button>
                    </td>
                    <td style={tdStyle}>
                      <button type="button" className="btn btn-sm btn-danger" onClick={() => setActionItem(item)}>
                        <i className="bi bi-three-dots-vertical me-1"></i>Action
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {actionItem ? (
        <div onClick={() => setActionItem(null)} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.35)", backdropFilter: "blur(4px)", zIndex: 4000, display: "flex", alignItems: "center", justifyContent: "center", padding: "18px" }}>
          <div onClick={(event) => event.stopPropagation()} style={{ width: "min(420px, 100%)", background: "#fff", borderRadius: "18px", padding: "20px", boxShadow: "0 28px 60px rgba(15,23,42,0.24)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center", marginBottom: "14px" }}>
              <div>
                <h4 style={{ margin: 0, color: "#0f172a" }}>Feedback Action</h4>
                <p style={{ margin: "4px 0 0", color: "#64748b" }}>{actionItem.student_name || "Student"}</p>
              </div>
              <button type="button" className="btn btn-light" onClick={() => setActionItem(null)}><i className="bi bi-x-lg"></i></button>
            </div>
            <div style={{ display: "grid", gap: "10px" }}>
              <button type="button" className="btn btn-success" onClick={() => updateStatus(actionItem, "approved")}>
                <i className="bi bi-check-circle me-1"></i>Approve
              </button>
              <button type="button" className="btn btn-warning" onClick={() => updateStatus(actionItem, "rejected")}>
                <i className="bi bi-x-circle me-1"></i>Reject
              </button>
              <button type="button" className="btn btn-danger" onClick={() => deleteFeedback(actionItem)}>
                <i className="bi bi-trash me-1"></i>Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </DashboardLayout>
  );
}
